import {
  Component,
  input,
  model,
  output,
  computed,
  inject,
  signal,
  WritableSignal,
  OnInit,
  effect,
  untracked
} from "@angular/core";
import { CommonModule, DatePipe, TitleCasePipe } from "@angular/common";
import { ReactiveFormsModule, FormControl, FormGroup, FormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from "@angular/forms";
import { DatePickerModule } from "primeng/datepicker";
import { VendorSelectionDrawerComponent } from "../vendor-selection-drawer/vendor-selection-drawer.component";
import { PhotoGridComponent } from "../photo-grid/photo-grid.component";
import { getBqStepDisplay } from "../../../../../../../domains/jobs/models/build-quality-step-display";
import { OpenItemSelection } from "../../open-items.component";
import { BlobService } from "../../../../../../../shared/services/blob.service";
import { LoggerService } from "../../../../../../../shared/services/logger.service";
import { CheckboxModule } from "primeng/checkbox";
import { ImageCompressionPipelineService } from "../../../../../../../domains/media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../../../domains/media/utils/media-order.util";
import { ImageAnnotatedCallback } from "../../../../../../../domains/media/components/images-modal/images-modal.component";
import { JobIssueVendorWithDetails, JobIssueWithDetails } from "../../../../../../../domains/jobIssues/models/JobIssueWithDetails";
import { DropdownModule } from "primeng/dropdown";
import { Area, ImageType, JobIssueVendor, Media, UserVendorContact } from "../../../../../../../core/database/db";
import { ButtonModule } from "primeng/button";
import { StandardBottomSheetComponent } from "../../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { FormFieldComponent } from "../../../../../../../shared/components/form-field/form-field.component";
import { TextareaComponent } from "../../../../../../../shared/components/textarea/textarea.component";
import { CONSTANTS } from "../../../../../../../constants";
import { AssignRoomBottomSheetComponent } from "../assign-rooms/assign-room-bottom-sheet.component";
import { BqaImageItem } from "../../../../../../../domains/media/components/images-modal/bqa-image-item";
import { OpenItemsDataService } from "../../../services/open-items-data.service";
import { SelectModule } from "primeng/select";
import { SendToContactComponent } from "../send-to-contact/send-to-contact.component";
import { SELF_ASSIGNED_VENDOR, SELF_ASSIGNED_VENDOR_ASSIGNMENT, VendorDTO } from "../../../../../../../domains/vendors/models/VendorDTO";
import { UiToastService } from "../../../../../../../shared/components/toast/ui-toast.service";
import { VendorDeliveryState } from "../../../../../../../domains/vendors/models/JobIssueVendorDTO";

export enum IssueItemErrorState {
  NoError,
  NoVendor,
  NoSelectedContact,
  InvalidEmail
}

export enum IssueExpansionState {
  Collapsed,
  Normal,
  Expanded
}

export interface ContactChangeEvent {
  issue: JobIssueWithDetails;
  vendorAssignment: JobIssueVendor;
  contacts: UserVendorContact[];
}

interface IssueTag {
  id: number;
  text: string;
  type: "vendor" | "due-date" | "sent" | "viewed" | "error" | "error-text" | "vendor-selected" | "assign-room" | "room" | "build-quality-step"
  icon?: string;
  shouldHideOnExpand: boolean;
}

interface IssueItemForm {
  notes: FormControl<string>;
  photos: FormControl<WritableSignal<Media[]>>;
  dueDate: FormControl<Date | null>;
}

@Component({
  selector: "app-issue-item",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    DatePickerModule,
    VendorSelectionDrawerComponent,
    CheckboxModule,
    ButtonModule,
    StandardBottomSheetComponent,
    FormFieldComponent,
    TextareaComponent,
    AssignRoomBottomSheetComponent,
    ReactiveFormsModule,
    SelectModule,
    SendToContactComponent,
    PhotoGridComponent
  ],
  providers: [DatePipe, TitleCasePipe],
  templateUrl: "./issue-item.component.html",
  styleUrls: ["./issue-item.component.scss"]
})
export class IssueItemComponent implements OnInit {
  maxNoteLength = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  SELF_ASSIGNED_VENDOR = SELF_ASSIGNED_VENDOR;
  VendorDeliveryState = VendorDeliveryState;

  private openItemsDataService = inject(OpenItemsDataService);
  private toastService = inject(UiToastService);
  private formBuilder = inject(FormBuilder);
  titleCasePipe = inject(TitleCasePipe);

  constructor(
    private blobService: BlobService,
    private logger: LoggerService,
    private datePipe: DatePipe,
    private imageCompressionPipelineService: ImageCompressionPipelineService
  ) {
    effect(() => {
      if (this.isSavingIssue() || this.isSavingVendor() || this.isMarkingComplete()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
    effect(() => {
      // Unfiltered (includes deleted) so the merge below can detect removals vs. not-yet-synced items.
      const latestMediaForIssue = this.media().filter(m => m.imageType === ImageType.Issue && m.parentIdentifier === this.issue().identifier);
      const photos = this.form.controls.photos.value;
      // untracked: avoid re-running this effect on our own writes (e.g. from onPhotosSelected).
      const merged = untracked(() => this.mergeIssueMedia(photos(), latestMediaForIssue));
      photos.set(merged);
      this.syncPhotosRequired();

      this.form.get("photos")!.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true
      });
    });
  }

  issue = input.required<JobIssueWithDetails>();
  openItemsSelectionType = input<OpenItemSelection>(OpenItemSelection.Room);
  isChecked = model<boolean>(false);
  isCollapsed = model<boolean>(false);
  isExpanded = model<boolean>(false);
  checkedVendorContactsMap = input<Record<number, UserVendorContact[]>>({});
  isVendorDrawerOpen = model<boolean>(false);
  showEditTextSheet = model<boolean>(false);
  showAssignRoomSheet = model<boolean>(false);
  formSubmitted = signal<boolean>(false);
  isMarkingComplete = signal<boolean>(false);
  currentlyEditingVendorAssignment = signal<JobIssueVendorWithDetails | null>(null);
  isSavingIssue = signal<boolean>(false);
  isSavingVendor = signal<boolean>(false);

  onIssueChecked = output<boolean>();
  onIssueExpanded = output<boolean>();
  onContactSelectionChange = output<ContactChangeEvent>();

  tags = computed<IssueTag[]>(() => {
    const tags: IssueTag[] = [];
    let idIndex = 1;
    if (this.issue().isAreaUnassigned) {
      tags.push({ id: idIndex++, text: "Assign Room", type: "assign-room", shouldHideOnExpand: false });
    }
    if (this.issue().dueDate) {
      tags.push({ id: idIndex++, text: `Due: ${this.datePipe.transform(this.issue().dueDate, "MM/dd/yyyy")}`, type: "due-date", shouldHideOnExpand: true });
    }
    if (this.issue().ancestorJobIssues && this.issue().ancestorJobIssues.length > 0) {
      const ancestorIssuesToShow = this.issue().ancestorJobIssues.filter(ancestor => ancestor.buildQualityStep !== this.issue().buildQualityStep);
      for (const ancestorStep of ancestorIssuesToShow) {
        const step = ancestorStep.buildQualityStep;
        if (step !== null && step !== undefined) {
          tags.push({
            id: idIndex++,
            text: getBqStepDisplay(step),
            type: "build-quality-step",
            shouldHideOnExpand: false
          });
        }
      }
    }
    const buildQualityStep = this.issue().buildQualityStep;
    if (buildQualityStep !== null && buildQualityStep !== undefined) {
      tags.push({
        id: idIndex++,
        text: getBqStepDisplay(buildQualityStep),
        type: "build-quality-step",
        shouldHideOnExpand: false
      });
    }
    if (this.issue().areaName) {
      tags.push({ id: idIndex++, text: this.issue().areaName!, type: "room", icon: "pi-home", shouldHideOnExpand: true });
    }
    if (this.issue().isSelfAssigned) {
      tags.push({ id: idIndex++, text: "Assigned to Me", type: "vendor", shouldHideOnExpand: true });
    }
    this.issue().assignedVendors.forEach(vendor => {
      let type: IssueTag["type"] = "vendor";
      let icon = undefined;
      if (vendor.vendorDeliveryState === VendorDeliveryState.ContactRequired) {
        type = "error";
        icon = "pi-exclamation-circle";
      } else if (vendor.vendorDeliveryState === VendorDeliveryState.Sent) {
        type = "sent";
        icon = "pi-send";
      } else if (vendor.vendorDeliveryState === VendorDeliveryState.Viewed) {
        type = "viewed";
        icon = "pi-eye";
      } else if (vendor.vendorDeliveryState === VendorDeliveryState.NotSent) {
        type = "vendor-selected";
      }
      tags.push({ id: idIndex++, text: this.titleCasePipe.transform(vendor.vendorDetails.doingBusinessAs || vendor.vendorDetails.name), type, icon, shouldHideOnExpand: true });
    });

    return tags;
  });

  media = computed(() => this.openItemsDataService.media() ?? []);
  lot = computed(() => this.openItemsDataService.lot() ?? (() => {
    throw new Error("Lot data is not available in OpenItemsDataService.");
  })());
  communityId = computed(() => this.openItemsDataService.financialCommunityId() ?? (() => {
    throw new Error("Community ID is not available in OpenItemsDataService.");
  })());

  issueRequiresContact = computed(() => {
    return this.tags().some(x => x.type === "error");
  });

  form: FormGroup<IssueItemForm> = this.formBuilder.group({
    notes: new FormControl("", { nonNullable: true, validators: Validators.required }),
    photos: new FormControl<WritableSignal<Media[]>>(signal<Media[]>([]), this.hasMediaValidator.bind(this)) as FormControl<WritableSignal<Media[]>>,
    dueDate: new FormControl<Date | null>(null)
  });

  galleryItems = computed(() =>
    this.form.controls.photos.value()
      .filter(img => !img.deleted && (img.base64Image || img.resourceUri))
      .map(image => {
        const src = image.base64Image
          ? URL.createObjectURL(this.blobService.base64ToBlob(image.base64Image))
          : (image.resourceUri as string);
        return new BqaImageItem({
          azureBlobUri: src,
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      }));

  vendors = computed(() => {
    const vendors = [...(this.openItemsDataService.vendors() ?? [])];

    vendors.unshift(SELF_ASSIGNED_VENDOR);

    return vendors;
  });

  shouldShowRemoveButton = computed(() => this.issue().assignedVendors.length > 0 || this.issue().isSelfAssigned);

  assignedVendors = computed(() => {
    const vendors = [...(this.issue().assignedVendors ?? [])];
    if (this.issue().isSelfAssigned) {
      vendors.unshift({
        ...SELF_ASSIGNED_VENDOR_ASSIGNMENT,
        vendorDetails: {
          ...SELF_ASSIGNED_VENDOR,
          userVendorContacts: []
        },
        vendorDeliveryState: VendorDeliveryState.NotSent,
        issueIdentifier: this.issue().identifier,
      });
    }
    return vendors;
  });

  assignedVendorIds = computed(() => this.assignedVendors().map(v => v.externalVendorId));

  sendableVendors = computed(() => {
    return this.assignedVendors().filter(v => v.externalVendorId !== SELF_ASSIGNED_VENDOR.id);
  });

  IssueItemErrorState = IssueItemErrorState;
  OpenItemSelectionEnum = OpenItemSelection;

  /**
   * BQ1 (Discuss) items can be logged without photos, matching the BQ1 issue drawer.
   * Kept as a field because the form's validator runs before the `issue` input is available.
   */
  private photosRequired = true;

  private syncPhotosRequired(): void {
    this.photosRequired = this.issue().buildQualityStep !== CONSTANTS.BUILD_QUALITY_STEPS.BQ1;
  }

  ngOnInit(): void {
    this.syncPhotosRequired();
    const mediaForIssue = this.media().filter(m => m.imageType === ImageType.Issue && m.parentIdentifier === this.issue().identifier && !m.deleted);

    // Keep the existing photos signal reference (don't replace it) so anything already
    // subscribed to it (e.g. galleryItems) keeps receiving updates.
    this.form.reset({
      notes: this.issue().text,
      photos: this.form.controls.photos.value,
      dueDate: this.issue().dueDate ? new Date(this.issue().dueDate!) : null
    });
    this.form.controls.photos.value.set(mediaForIssue);
  }

  getPrimaryDisplayName() {
    if (this.openItemsSelectionType() === OpenItemSelection.Room) {
      return this.issue()?.areaName;
    } else if (this.openItemsSelectionType() === OpenItemSelection.Vendor) {
      let displayName: string | undefined = undefined;
      if (this.assignedVendors() && this.assignedVendors().length > 0) {
        displayName = this.assignedVendors()
          .map(v => this.titleCasePipe.transform(v.vendorDetails.name))
          .join(", ");
      }
      return displayName;
    } else if (this.openItemsSelectionType() === OpenItemSelection.Type) {
      return this.issue().componentName;
    }

    throw new Error(`Unsupported open item selection type: ${this.openItemsSelectionType()}`);
  }

  onCheckboxChecked(event: boolean) {
    this.isChecked.set(event);
    this.onIssueChecked.emit(event);
  }

  onToggleExpansion($event: Event) {
    this.isExpanded.set(!this.isExpanded());
    this.onIssueExpanded.emit(this.isExpanded());
    setTimeout(() => {
      ($event.target as HTMLElement).closest(".issue-item")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  onEditText(): void {
    this.formSubmitted.set(false);
    this.showEditTextSheet.set(true);
  }

  async onSaveEditText(): Promise<void> {
    this.formSubmitted.set(true);
    if (this.form.invalid) {
      return;
    }

    this.isSavingIssue.set(true);

    const updatedText = this.form.controls.notes.value;
    await this.openItemsDataService.updateIssue(this.issue(), { text: updatedText }, []);

    this.showEditTextSheet.set(false);
    this.formSubmitted.set(false);
    this.isSavingIssue.set(false);
  }

  onCancelEditText(): void {
    this.showEditTextSheet.set(false);
    this.formSubmitted.set(false);
    this.form.controls.notes.reset(this.issue().text);
  }

  onAssignRoom(): void {
    if (this.issue().isAreaUnassigned) {
      this.showAssignRoomSheet.set(true);
    }
  }

  async onRoomAssigned(area: Area): Promise<void> {
    this.isSavingIssue.set(true);

    const updatedAreaIdentifier = area.identifier;
    await this.openItemsDataService.updateIssue(this.issue(), { lotAreaIdentifier: updatedAreaIdentifier }, []);
    this.toastService.success("Room assigned successfully");
    setTimeout(() => {
      document.querySelector(`.issue-item-${this.issue().id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    this.isSavingIssue.set(false);

  }

  onVendorSelectClick(vendor?: JobIssueVendorWithDetails): void {
    this.currentlyEditingVendorAssignment.set(vendor || null);
    this.isVendorDrawerOpen.set(true);
  }

  async onVendorSelected(vendor: VendorDTO): Promise<void> {
    this.isSavingVendor.set(true);

    await this.openItemsDataService.addOrReplaceVendorAssignmentForIssue(this.issue(), vendor, this.currentlyEditingVendorAssignment());

    this.currentlyEditingVendorAssignment.set(null);
    this.isVendorDrawerOpen.set(false);

    this.isSavingVendor.set(false);
  }

  async onRemoveVendor(vendorToRemove: JobIssueVendor): Promise<void> {
    this.isSavingVendor.set(true);

    await this.openItemsDataService.removeVendorFromIssue(this.issue(), vendorToRemove);

    this.isSavingVendor.set(false);
  }

  onVendorSelectionCanceled(): void {
    this.isVendorDrawerOpen.set(false);
  }

  async onMarkComplete(): Promise<void> {
    this.isMarkingComplete.set(true);
    await this.openItemsDataService.updateIssue(this.issue(), { isComplete: true }, []);
    this.isMarkingComplete.set(false);
  }

  async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }

    const mediaRecords = await this.imageCompressionPipelineService.encode(files, this.issue().identifier, ImageType.Issue);

    this.form.controls.photos.value.update(currentMedia => insertAt(currentMedia, mediaRecords, insertIndex));

    this.imageCompressionPipelineService.compress(mediaRecords, async compressedMediaRecords => {
      this.form.controls.photos.value.update(currentMedia => {
        return currentMedia.map(originalImage => {
          const compressedImage = compressedMediaRecords.find(m => m.fileIdentifier === originalImage.fileIdentifier);
          if (compressedImage) {
            const updatedRecord: Media = {
              ...originalImage,
              base64Image: compressedImage.base64Image,
              size: compressedImage.size,
              mimeType: compressedImage.mimeType
            };
            return updatedRecord;
          }
          return originalImage;
        });
      });

      this.form.get("photos")!.markAsDirty();
      this.form.get("photos")!.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true
      });
      this.form.updateValueAndValidity({
        emitEvent: true
      });


      const media = this.form.controls.photos.value();
      await this.openItemsDataService.updateIssue(this.issue(), {}, media);
    });

    return mediaRecords;
  }

  protected readonly annotateImage: ImageAnnotatedCallback = (originalFileIdentifier, annotatedFile) =>
    this.handleImageAnnotated(originalFileIdentifier, annotatedFile);

  async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    // A saved original is soft-deleted (then dropped by the media merge), so inserting at its index keeps the slot.
    const index = this.form.controls.photos.value().findIndex(photo => photo.fileIdentifier === originalFileIdentifier);
    await this.onImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  async onImageDeleted(fileIdentifier: string): Promise<void> {
    try {
      const photos = this.form.controls.photos.value();

      const mediaToBeDeleted = photos.find(photo => photo.fileIdentifier === fileIdentifier);
      const isExistingMedia = !!mediaToBeDeleted && !!mediaToBeDeleted.id;
      if (isExistingMedia) {
        this.form.controls.photos.value.update(currentMedia => currentMedia.map(photo => (photo.fileIdentifier === fileIdentifier ? { ...photo, deleted: 1, deletedAt: new Date() } : photo)));
      } else {
        this.form.controls.photos.value.update(currentMedia => currentMedia.filter(photo => photo.fileIdentifier !== fileIdentifier));
      }
      this.form.get("photos")!.markAsDirty();
      this.form.get("photos")!.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true
      });
      this.form.updateValueAndValidity({
        emitEvent: true
      });

      const media = this.form.controls.photos.value();
      await this.openItemsDataService.updateIssue(this.issue(), {}, media);

    } catch (error) {
      this.logger.error("Error deleting media", error);
    }
  }

  async onDueDateChange(): Promise<void> {
    this.isSavingIssue.set(true);

    const updatedDate = this.form.controls.dueDate.value ?? undefined;
    await this.openItemsDataService.updateIssue(this.issue(), { dueDate: updatedDate }, []);

    this.isSavingIssue.set(false);
  }

  onSelectionChange(selected: UserVendorContact[], vendor: JobIssueVendor): void {
    this.onContactSelectionChange.emit({
      issue: this.issue(),
      vendorAssignment: vendor,
      contacts: selected
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted()));
  }

  hasMediaValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.photosRequired) {
      return null;
    }
    const hasMedia = control.value()?.filter((x: Media) => x.deleted === 0).length > 0;
    return hasMedia ? null : { required: true };
  };

  /**
   * Reconciles the form's current photos with a freshly-read media list for this issue.
   * Items not yet present in `latest` (e.g. an upload still in flight/not yet synced into
   * the shared media signal) are preserved rather than dropped, items present in both are
   * refreshed with the latest data, items marked deleted in `latest` are removed, and any
   * brand new items from `latest` are appended.
   */
  private mergeIssueMedia(current: Media[], latest: Media[]): Media[] {
    const latestByIdentifier = new Map(latest.map(m => [m.fileIdentifier, m]));
    const currentIdentifiers = new Set(current.map(m => m.fileIdentifier));

    const reconciledExisting = current
      .map(photo => latestByIdentifier.get(photo.fileIdentifier) ?? photo)
      .filter(photo => !photo.deleted);

    const newFromLatest = latest.filter(m => !m.deleted && !currentIdentifiers.has(m.fileIdentifier));

    return [...reconciledExisting, ...newFromLatest];
  }
}
